/**
 * 开发预览：分镜「场景参考图」缩略图 mock（外链样图）。
 * 产品规则：列表仅在有真实参考图时显示缩略图；mock 须显式开启 env，不进入生产默认行为。
 */

export type ComicReferenceAspectMock = {
  ratio: string
  /** 可直接用于 img src 的 HTTPS 地址 */
  url: string
}

/**
 * picsum 固定 id + 真实像素比例；方框内 object-cover 裁切时，横图/竖图裁掉区域应明显不同。
 * 开发默认 `cycle` 按格轮换；设 `VITE_COMIC_REFERENCE_MOCK_MODE=1x1` 可只看方图裁切。
 */
export const COMIC_REFERENCE_ASPECT_MOCKS: readonly ComicReferenceAspectMock[] = [
  { ratio: '1:1', url: 'https://picsum.photos/id/1015/400/400' },
  { ratio: '3:4', url: 'https://picsum.photos/id/1018/300/400' },
  { ratio: '2:3', url: 'https://picsum.photos/id/1025/400/600' },
  { ratio: '4:3', url: 'https://picsum.photos/id/1035/400/300' },
  { ratio: '16:9', url: 'https://picsum.photos/id/1043/640/360' },
  { ratio: '9:16', url: 'https://picsum.photos/id/1050/360/640' },
] as const

/** `1x1`：每格同一张方图；`cycle`：按格轮换各比例样图（开发默认） */
export type ComicReferenceMockMode = '1x1' | 'cycle'

/** 仅当 `VITE_COMIC_REFERENCE_ASPECT_MOCK=true` 时为 true（生产默认 false） */
export function isComicReferenceAspectMockEnabled(): boolean {
  return import.meta.env.VITE_COMIC_REFERENCE_ASPECT_MOCK === 'true'
}

export function hasPanelReferenceImage(realUrl: string | undefined): boolean {
  return Boolean(realUrl?.trim())
}

/**
 * 开发：指定格在列表里强制不显示参考图缩略图（不落库、不用 mock 填）。
 * `VITE_COMIC_REFERENCE_DEV_EMPTY`：
 * - `all` 全部无图
 * - `alternate` / `odd`：格 1、3、5…（1-based）无图
 * - `even`：格 2、4、6… 无图（仅 1 格时格 1 仍可显示，适合本地试效果）
 * - `1,3,5`：指定格号（1-based，逗号分隔）
 */
export function isComicReferenceDevEmptySimulationEnabled(): boolean {
  return (
    import.meta.env.DEV &&
    Boolean(import.meta.env.VITE_COMIC_REFERENCE_DEV_EMPTY?.trim())
  )
}

export function isComicReferenceDevEmptySlot(slotIndex: number): boolean {
  const spec = import.meta.env.VITE_COMIC_REFERENCE_DEV_EMPTY?.trim().toLowerCase()
  if (!spec || !import.meta.env.DEV) return false

  if (spec === 'all') return true

  const oneBasedSlot = slotIndex + 1
  if (spec === 'alternate' || spec === 'odd') {
    return oneBasedSlot % 2 === 1
  }
  if (spec === 'even') {
    return oneBasedSlot % 2 === 0
  }

  const targets = spec
    .split(/[,;\s]+/)
    .map((part) => Number.parseInt(part, 10))
    .filter((n) => Number.isFinite(n) && n >= 1)
  return targets.length > 0 && targets.includes(oneBasedSlot)
}

/** @deprecated 使用 {@link isComicReferenceDevEmptySlot} */
export function simulateEmptyPanelReferenceUrl(
  realUrl: string | undefined,
  slotIndex: number,
): string | undefined {
  if (isComicReferenceDevEmptySlot(slotIndex)) return undefined
  return realUrl
}

/**
 * 分镜列表是否渲染参考图缩略图。
 * 被 DEV_EMPTY 命中的格：强制无图；其余格：有真图或（非空格子 + mock 开启）时显示。
 */
export function shouldShowComicReferenceThumb(
  rawUrl: string | undefined,
  slotIndex: number,
): boolean {
  if (isComicReferenceDevEmptySlot(slotIndex)) return false
  return hasPanelReferenceImage(rawUrl) || isComicReferenceAspectMockEnabled()
}

export function getComicReferenceMockMode(): ComicReferenceMockMode {
  const mode = import.meta.env.VITE_COMIC_REFERENCE_MOCK_MODE?.trim()
  if (mode === '1x1') return '1x1'
  if (mode === 'cycle' || mode === 'ratios') return 'cycle'
  // 未配置时：开发态按格轮换，便于对比各比例在方框里的裁切
  return import.meta.env.DEV ? 'cycle' : '1x1'
}

const ONE_X_ONE = COMIC_REFERENCE_ASPECT_MOCKS[0]!.url

export function getComicReferenceAspectMockForSlot(
  slotIndex: number,
): ComicReferenceAspectMock {
  const mocks = COMIC_REFERENCE_ASPECT_MOCKS
  const i = ((slotIndex % mocks.length) + mocks.length) % mocks.length
  return mocks[i]!
}

/**
 * 开发态预览用图：优先真实参考图；无图时用外链样图。
 * `forceMock` 为 true 时始终用样图（便于对比裁切，不依赖图库）。
 */
export function resolveComicReferenceThumbPreviewUrl(
  realUrl: string | undefined,
  slotIndex: number,
  options?: { forceMock?: boolean },
): string {
  const real = realUrl?.trim() ?? ''
  const mockOn = isComicReferenceAspectMockEnabled()
  const forceMock = options?.forceMock === true

  if (real && !forceMock) return real
  if (!mockOn) return real

  if (getComicReferenceMockMode() === '1x1') {
    return ONE_X_ONE
  }

  return getComicReferenceAspectMockForSlot(slotIndex).url
}

export type ComicReferenceThumbPreview = {
  url: string
  /** 开发 mock 时对应样图比例，用于角标 */
  mockRatio?: string
}

function isUsingComicReferenceMock(
  realUrl: string | undefined,
  options?: { forceMock?: boolean },
): boolean {
  const real = realUrl?.trim() ?? ''
  if (!isComicReferenceAspectMockEnabled()) return false
  if (options?.forceMock === true) return true
  return !real
}

/**
 * 缩略图 URL +（开发 mock 时）比例标签。
 */
export function resolveComicReferenceThumbPreview(
  realUrl: string | undefined,
  slotIndex: number,
  options?: { forceMock?: boolean },
): ComicReferenceThumbPreview {
  const url = resolveComicReferenceThumbPreviewUrl(realUrl, slotIndex, options)
  if (!url || !isUsingComicReferenceMock(realUrl, options)) {
    return { url }
  }
  if (getComicReferenceMockMode() === '1x1') {
    return { url, mockRatio: '1:1' }
  }
  return { url, mockRatio: getComicReferenceAspectMockForSlot(slotIndex).ratio }
}

/** @deprecated 使用 {@link resolveComicReferenceThumbPreviewUrl} */
export function getComicReferenceAspectMockUrl(slotIndex: number): string {
  return resolveComicReferenceThumbPreviewUrl(undefined, slotIndex)
}
